import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';

import { containerStyles } from '../../../styles/layout';
import { headers } from '../../../styles/type';
import { colors } from '../../../theme/colors';
import { media } from '../../../theme/media';
import { rem } from '../../../utilities/style';

import Image from '../../Image';
import TextRenderer from '../../TextRenderer';
import ScrollSyncItem from './ScrollSyncItem';

const Container = styled.div`
  padding: ${rem(32)} 0;

  ${media('>tiny')} {
    padding: ${rem(56)} 0;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Subhead = styled.p`
  ${headers.h6}
  margin-bottom: ${rem(12)};
  text-align: center;
  color: ${colors.blue['500']};
`;

const Headline = styled.h2`
  ${headers.h3}
  text-align: center;
  color: ${colors.blue['800']};

  ${media('>=mini')} {
    margin-bottom: ${rem(40)};
  }
`;

const Layout = styled.div`
  position: relative;
  display: grid;
  align-items: start;
  grid-template-columns: ${rem(24)} 1fr;
  grid-template-areas: 'image items';

  ${media('>=mini')} {
    grid-template-columns: 1fr 1fr;
    gap: ${rem(40)};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${rem(4)};
    display: block;
    width: 1px;
    height: 100%;
    background: linear-gradient(
      ${colors.gray['600']} 0%,
      ${colors.gray['600']} 50%,
      transparent 50%,
      transparent 100%
    );
    background-size: 1px ${rem(8)};
    background-repeat: repeat-y;
    mask-image: linear-gradient(
      to bottom,
      transparent 0%,
      black 10%,
      black 90%,
      transparent 100%
    );

    ${media('>=mini')} {
      left: 50%;
    }
  }
`;

const ImageContainer = styled.div`
  grid-area: image;
  position: sticky;
  top: ${rem(120)};
  margin: ${rem(40)} 0 ${rem(20)};
  display: none;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  grid-template-areas: 'stack';

  ${media('>=mini')} {
    display: grid;
  }
`;

const StyledImage = styled(Image)`
  grid-area: stack;
  max-width: ${rem(560)};
  aspect-ratio: 560 / 370;
  border-radius: ${rem(8)};
  opacity: 0;

  ${({ currentIndex }) => currentIndex && `
    opacity: 1;
  `}
`;

const Track = styled.div`
  grid-area: items;
  display: grid;

  ${media('>=mini')} {
    gap: ${rem(80)};
  }
`;

const Item = styled.div`
  position: relative;
  margin: ${rem(48)} 0;
  padding: 0 ${rem(16)};

  ${media('>=mini')} {
    margin: ${rem(64)} 0;
  }

  ${media('>=small')} {
    padding: 0 ${rem(24)};
  }

  ${media('>=smedium')} {
    padding: 0 ${rem(48)};
  }

  ${media('>=large')} {
    padding: 0 ${rem(64)};
  }

  &::before {
    content: '';
    position: absolute;
    top: ${rem(12)};
    left: ${rem(-20)};
    width: ${rem(9)};
    height: ${rem(9)};
    margin-left: ${rem(-4)};
    border-radius: 50%;
    background: ${colors.gray['600']};

    ${({ currentIndex }) => currentIndex && `
      background: ${colors.blue['500']};
      box-shadow: 0 0 0 ${rem(4)} ${colors.blue['100']};
    `}

    ${media('>=mini')} {
      left: ${rem(-20)};
    }
  }
`;

const ScrollSync = ({ subhead, headline, items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const handleCurrentIndex = (index) => {
    setCurrentIndex(index);
  };

  return (
    <Container>
      <ContentWrapper>
        {subhead && (
          <Subhead>
            <TextRenderer text={subhead} />
          </Subhead>
        )}
        {headline && (
          <Headline>
            <TextRenderer text={headline} />
          </Headline>
        )}

        <Layout>
          <ImageContainer>
            {items?.map((item, index) => (
              item.image && (
                <StyledImage
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  currentIndex={currentIndex === index}
                  image={item.image}
                  alt=""
                />
              )
            ))}
          </ImageContainer>

          <Track>
            {items?.map((item, index) => (
              <Item
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                currentIndex={currentIndex === index}
              >
                <ScrollSyncItem
                  image={item.image}
                  subhead={item.subhead}
                  headline={item.headline}
                  text={item.text}
                  primaryCtaText={item.primaryCtaText}
                  primaryCtaLink={item.primaryCtaLink}
                  secondaryCtaText={item.secondaryCtaText}
                  secondaryCtaLink={item.secondaryCtaLink}
                  index={index}
                  indexIntersectCallback={handleCurrentIndex}
                />
              </Item>
            ))}
          </Track>
        </Layout>
      </ContentWrapper>
    </Container>
  );
};

ScrollSync.propTypes = {
  subhead: PropTypes.string,
  headline: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.arrayOf(
    PropTypes.object
  ),
};

export const query = graphql`
  fragment ScrollSyncFragment on ContentfulScrollSync {
    id
    __typename
    subhead
    headline
    items {
      id
      subhead
      headline
      text {
        raw
      }
      primaryCtaText
      primaryCtaLink
      secondaryCtaText
      secondaryCtaLink
      image {
        gatsbyImageData(width: 560, height: 370, quality: 75, resizingBehavior: FILL)
      }
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default ScrollSync;
